import { useEffect, useMemo } from "react";
import NoData from "@/components/NoData";
import LtdLoading from "@/components/LichThiDauFull/LtdLoading";
import Match from "@/components/LichThiDauFull/Match";
import useIsDesktop from "@/components/LichThiDauFull/useIsDesktop";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import { getRoundLabel } from "@/utils/leagueFixturesByLeague";
import CountryLeagueFilter from "./CountryLeagueFilter";
import useLichThiDauByCountry from "./useLichThiDauByCountry";
import "@/components/LichThiDauFull/index.css";

/**
 * @param {object} props
 * @param {string} props.country
 * @param {'tatca'|'ketqua'} [props.type]
 */
export default function LichThiDauByCountry({ country: countrySlug, type = "tatca" }) {
  const isDesktop = useIsDesktop();
  const { clearAllData } = useLiveData();
  const {
    themeStyle,
    leagues,
    selectedId,
    setSelectedId,
    leaguesWithFixtures,
    fixturesMap,
    waiting,
    failed,
    countryLoading,
  } = useLichThiDauByCountry(countrySlug, type);

  const matchIds = useMemo(() => {
    const ids = [];
    for (const league of leaguesWithFixtures) {
      const rounds = fixturesMap[String(league.id)] || [];
      for (const [, matches] of rounds) {
        for (const m of matches ?? []) {
          const n = Number(m.id);
          if (Number.isFinite(n)) ids.push(n);
        }
      }
    }
    return ids;
  }, [fixturesMap, leaguesWithFixtures]);

  useLiveUpdates(matchIds);

  useEffect(
    () => () => {
      clearAllData();
    },
    [clearAllData],
  );

  return (
    <div
      className="kbv-ltd-full flex flex-col gap-2 bg-[var(--theme-bg)] text-[var(--theme-text)]"
      style={themeStyle}
    >
      <div className="kbv-ltd-filter-block">
        <CountryLeagueFilter
          leagues={leagues}
          selectedId={selectedId}
          onSelect={setSelectedId}
          themeStyle={themeStyle}
          loading={countryLoading}
        />
      </div>
      <div className="kbv-bycountry-scroll">
        {failed ? (
          <NoData message="Không tìm thấy quốc gia" />
        ) : waiting ? (
          <LtdLoading />
        ) : leaguesWithFixtures.length === 0 ? (
          <NoData message="Không có trận đấu" />
        ) : (
          <div className="flex flex-col gap-4">
            {leaguesWithFixtures.map((league) => {
              const roundEntries = fixturesMap[String(league.id)] || [];
              return (
                <div key={league.id} className="flex flex-col gap-2">
                  <div className="kbv-ltd-league-head">
                    <div className="flex min-w-0 flex-1 items-center gap-1 px-2 py-2">
                      {league?.logo ? (
                        <img
                          src={league.logo}
                          alt=""
                          className="w-5 h-5 object-contain shrink-0"
                          width={20}
                          height={20}
                        />
                      ) : null}
                      <span className="truncate text-sm font-semibold text-[var(--theme-text)]">
                        {league?.nameVN || league?.name || ""}
                      </span>
                    </div>
                  </div>
                  <div className="rounded p-0 bg-[var(--theme-foreground,#fff)] overflow-hidden border border-[var(--theme-border,#e5e7eb)]">
                    {roundEntries.map(([round, matches]) => (
                      <div
                        key={`${league?.id ?? ""}-${String(round)}`}
                        className="kbv-ltd-league-group flex flex-col min-h-0"
                      >
                        <div className="kbv-ltd-league-head">
                          <h3 className="m-0 truncate text-sm font-semibold text-[var(--theme-text)]">
                            {getRoundLabel(round, matches)}
                          </h3>
                        </div>
                        <div className="flex flex-col min-h-0">
                          {(matches || []).map((match, index) => (
                            <Match
                              key={match.id}
                              match={match}
                              horizontalLayout={isDesktop}
                              isLastItem={index === matches.length - 1}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
